import { call, put, select, takeLatest } from 'redux-saga/effects';
import { createAction } from '@reduxjs/toolkit';
import * as accountApi from '../../account/api/accountApi.js';
import { profileSaved } from '../../account/store/accountSlice.js';
import { onboardingCompleted, onboardingCompletionFailed, onboardingCompletionStarted } from './authSlice.js';
import { history } from '../../../lib/history.js';
import { captureSagaError } from '../../../lib/sentry.js';
import { toastShown } from '../../system/store/toastSlice.js';
import { syncStoredOnboardingPending } from '../lib/onboardingStatus.js';

/**
 * Each step dispatches onboardingStepSubmitted({ step, payload, isFinal }).
 *   step=1 payload={ user_type: string }
 *   step=2 payload={ primary_purpose: string }
 *   step=3 payload={ wa_optin: boolean, news_optin: boolean }
 *     - news_optin → updateNotifyPrefs('1'|'0') (legacy `checknotify`)
 *     - wa_optin   → HELD locally only. No confirmed backend field for a
 *       WhatsApp-reminders boolean (legacy stores a phone number as
 *       `whatsappNumber`, not a boolean opt-in). The toggle is captured in
 *       client state so the user sees the on/off behaviour; when the backend
 *       ships a field for this, swap the no-op for the real call.
 *   step=4 payload=full draft; save the merged profile first, then POST
 *     `onboarding` so completion is only recorded after both saves succeed.
 */
export const onboardingStepSubmitted = createAction('onboarding/stepSubmitted');
export const onboardingStepFailed = createAction('onboarding/stepFailed');
export const onboardingStepSucceeded = createAction('onboarding/stepSucceeded');

function friendly(err) {
  if (err?.response?.status === 401) {
    return 'We could not complete onboarding because this session was not accepted by the server. Please sign in again and try once more.';
  }
  if (err?.message === 'Network Error' || err?.code === 'ERR_NETWORK') {
    return "We couldn't reach the network. Check your connection and try again.";
  }
  return "We couldn't save that. Please try again.";
}

function stripEmptyValues(payload) {
  return Object.fromEntries(
    Object.entries(payload).filter(([, value]) => value !== undefined && value !== null && value !== ''),
  );
}

function toLegacyOnboardingPayload(payload = {}) {
  return stripEmptyValues({
    whatsappNumber: payload.whatsappNumber,
    isParent: payload.isParent ?? 0,
    receiveWhatsappNotification: payload.wa_optin ? 1 : 0,
    user_type: payload.user_type,
    childrenAge: payload.childrenAge,
    phone: payload.phone,
    isSubscribe: payload.news_optin ? 1 : 0,
    email: payload.email,
    full_name: payload.full_name,
  });
}

function* failCompletion(step, response) {
  const message = response?.message || "We couldn't save that. Please try again.";
  yield put(onboardingCompletionFailed(message));
  yield put(onboardingStepFailed({ step, error: message }));
  yield put(toastShown({ variant: 'error', message }));
}

export function* handleStepSubmitted(action) {
  const { step, payload, isFinal } = action.payload;
  try {
    if (isFinal) {
      yield put(onboardingCompletionStarted());
      const onboardingPayload = toLegacyOnboardingPayload(payload);
      // Sign-in loads the profile into auth.user. Prefer any newer account
      // edits, then merge the wizard answers so omitted fields are preserved.
      const currentProfile = yield select((state) => ({
        ...state.auth?.user,
        ...state.account?.profile,
      }));
      const profileBody = accountApi.serializeUserProfilePayload({
        ...accountApi.normalizeProfile(currentProfile),
        ...onboardingPayload,
        ...stripEmptyValues({ primary_purpose: payload.primary_purpose }),
      });
      const profileRes = yield call(accountApi.updateProfile, profileBody);
      if (!(profileRes?.status && profileRes.code === 200)) {
        yield* failCompletion(step, profileRes);
        return;
      }

      const res = yield call(accountApi.completeOnboarding, onboardingPayload);
      if (!(res?.status && res.code === 200)) {
        yield* failCompletion(step, res);
        return;
      }
      const savedProfile = accountApi.normalizeProfile(accountApi.serializeProfileBody({
        ...currentProfile,
        ...profileBody,
        onboarding: true,
      }));
      yield put(profileSaved(savedProfile));
      yield put(onboardingStepSucceeded({ step }));
      yield put(onboardingCompleted(savedProfile));
      syncStoredOnboardingPending(false);
      history.push('/courses');
      return;
    } else if (step === 3) {
      // Newsletter opt-in maps to the legacy `checknotify` endpoint.
      // WhatsApp opt-in is intentionally NOT persisted — see file header.
      yield call(accountApi.updateNotifyPrefs, payload.news_optin ? '1' : '0');
    } else {
      yield call(accountApi.updateProfile, payload);
    }
    yield put(onboardingStepSucceeded({ step }));
  } catch (err) {
    captureSagaError(err, { action: action.type, step });
    const msg = friendly(err);
    if (isFinal) {
      yield put(onboardingCompletionFailed(msg));
    }
    yield put(onboardingStepFailed({ step, error: msg }));
    yield put(toastShown({ variant: 'error', message: msg }));
  }
}

export default function* onboardingSaga() {
  yield takeLatest(onboardingStepSubmitted.type, handleStepSubmitted);
}
